import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { PrimaryButton } from "./OrgModal";
import { handleLogout } from "../auth/authHelpers";
import { fetchMe, ORG_STATUS, readSession } from "../../utils/organisationApi";
import { ORG_INACTIVE_EVENT } from "../../utils/planEvents";
import { resetPlanStore } from "../../utils/subscriptionData";

// How often an open page re-checks that its organisation is still active. A suspension is also noticed straight
// away by any request the server refuses for that reason, and when the tab comes back into view.
const CHECK_EVERY_MS = 90000;

const LAST_PLAN_PREFIX = "sifthire:last-plan:";

// Drops what the plan screens remembered, so no pricing or plan details outlive the organisation's access
const forgetPlan = () => {
  resetPlanStore();
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LAST_PLAN_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing remembered, nothing to forget
  }
};

// When the organisation is suspended or deleted while someone is using it, every page should say so at once,
// instead of some pages carrying on with what they loaded earlier while others start refusing requests.
// Mounted once in the main layout. A Super Admin who entered the organisation as support is not blocked: they
// are there to look at it.
const OrgInactiveGate = () => {
  const theme = useTheme();
  const session = useMemo(() => readSession(), []);
  const applies = Boolean(session.role) && !session.isSupportSession;
  const [inactive, setInactive] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!applies) return undefined;
    const trip = () => {
      forgetPlan();
      setInactive(true);
    };

    // 1. A request the server refused because the organisation is inactive
    window.addEventListener(ORG_INACTIVE_EVENT, trip);

    // 2. Asking directly: on load, when the tab returns, and every so often while it is open
    let stopped = false;
    const check = () => {
      if (stopped || document.visibilityState !== "visible") return;
      fetchMe()
        .then((me) => {
          const status = me?.organisation?.status;
          if (!stopped && (status === ORG_STATUS.SUSPENDED || status === ORG_STATUS.DELETED)) trip();
        })
        .catch(() => {
          // A refused request is reported by the interceptor; any other failure proves nothing
        });
    };
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener(ORG_INACTIVE_EVENT, trip);
    };
  }, [applies]);

  useEffect(() => {
    if (inactive) buttonRef.current?.focus();
  }, [inactive]);

  if (!inactive) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="org-inactive-title"
        aria-describedby="org-inactive-text"
        className="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-2xl"
        style={{ color: theme.palette.text.main }}
      >
        <h2 id="org-inactive-title" className="text-lg font-semibold">
          This organisation is suspended
        </h2>
        <p id="org-inactive-text" className="mt-2 text-sm text-accent-gray-light">
          You can't use it right now. Contact support to get access back.
        </p>
        <div className="mt-5 flex justify-center">
          <span ref={buttonRef} tabIndex={-1}>
            <PrimaryButton onClick={() => handleLogout()}>Sign out</PrimaryButton>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrgInactiveGate;
